
























































import { inject } from 'inversify-props';
import { Vue, Watch, Prop, Component } from 'vue-property-decorator';
import IAuthService from '../../../services/IAuthService';
import { InputValidation } from '../../../helpers/inputValidation';
import { Organization, PageResult } from '@models';

@Component
export default class OrganizationsSelector extends Vue {
  name = 'organization-selector';

  @Prop(String) id!: string;
  @Prop({ type: Object as () => Organization }) parent!: Organization;
  @Prop(Boolean) isOrganizationSearch!: boolean;
  @Prop(Boolean) isAdvertiserSearch!: boolean;
  @Prop(Function) onError: any;
  @Prop({ type: Boolean, default: true }) groupByOptionsByName!: boolean;

  @Prop({ type: Array as () => Organization[] }) value: Organization[];

  searchText: string = "";
  pageNumber: number = 1;
  pageSize: number = 20;
  options: Organization[] = [];
  selected: Organization[] = [];
  isLoading: boolean = false;
  noMoreOptions: boolean = false;
  optionsMax: number = 1000;

  @inject() private authService: IAuthService;
  input = InputValidation;

  async created() {
    await this.loadOrganizations();
  }

  reset() {
    this.options = [];
  }

  @Watch("parent")
  async loadDecendants(): Promise<void> {
    if (this.parent?.id) {
      this.reset();
      await this.loadOrganizations();
    }
    else {
      this.reset();
    }
  }

  get organizationOptions() {
    var organizations = this.options;

    if (!this.groupByOptionsByName) {
      return organizations;
    }

    let flags = [], output = [], l = organizations.length, i;

    this.value.forEach(x => flags[x.name.toLowerCase()] = true);

    for (i = 0; i < l; i++) {
      if (flags[organizations[i].name.toLowerCase()]) continue;
      flags[organizations[i].name.toLowerCase()] = true;
      output.push(organizations[i]);
    }

    return output;
  }

  async updateValue(value: Organization[]) {
    var result = value;

    if (value.length < this.value.length) { // on organization deselected
      

      if (this.groupByOptionsByName) {
        var removedOrgaizations =
          this.value.filter(previouslySelectedOrg => !value.includes(previouslySelectedOrg));

        var selectedOrganizationsWithSameNameAsRemoved =
          value.filter(selectedOrg => removedOrgaizations.some(removedOrg => selectedOrg.name.toLowerCase() == removedOrg.name.toLowerCase()));

        result = value.filter(selectedOrg => !selectedOrganizationsWithSameNameAsRemoved.includes(selectedOrg));
      }


    } else if (value.length > this.value.length) { // on organization selected
      if (this.isLoading) return;
      this.isLoading = true;

      if (this.groupByOptionsByName) {
        value = value.filter(x => !this.value.some(y => x.name.toLowerCase() == y.name.toLowerCase()));

        const isSearchTextStrict = true;
        let pageNumber = 1;
        let fetchedOrgsTotal = [];
        // fetch all organizations with the same names as the selected ones
        for (let i = 0; i < value.length; i++) {
          let fetchedOrgs = [];
          // initial fetch query
          let response: PageResult<Organization> = await this.fetchOrganizations(value[i].name, pageNumber, isSearchTextStrict);
          fetchedOrgs = response.data;
          pageNumber = response.pageNumber;
          let newlyFound = null;
          // keep querying for next page until all organizations are fetched
          while (response.totalCount > fetchedOrgs.length || (newlyFound != null && newlyFound.length == 0)) {
            response = await this.fetchOrganizations(value[i].name, pageNumber + 1, isSearchTextStrict);
            pageNumber = response.pageNumber;
            newlyFound = response.data.filter(x => !fetchedOrgs.some(y => y.id == x.id));
            fetchedOrgs = fetchedOrgs.concat(newlyFound);
          }
          newlyFound = fetchedOrgs.filter(x => !fetchedOrgsTotal.some(y => x.id == y.id));
          fetchedOrgsTotal = fetchedOrgsTotal.concat(newlyFound);
        }

        result = this.value.concat(fetchedOrgsTotal.filter(x => !value.some(y => x.id == y.id) && !this.value.some(y => x.id == y.id)));
        result = result.concat(value.filter(x => !result.some(y => x.id == y.id) && !this.value.some(y => x.id == y.id)));
      }
    }

    this.$emit('input', result);
    this.isLoading = false;
  }

  get selectedItemTags(): Organization[] {
    const flags: boolean[] = [];
    let result: Organization[] = [];
    const selectedItems: Organization[] = this.value;

    if (this.groupByOptionsByName) {
      for (let i = 0; i < selectedItems.length; i++) {
        if (flags[selectedItems[i].name.toLowerCase()]) continue;

        flags[selectedItems[i].name.toLowerCase()] = true;
        result.push(selectedItems[i]);
      }
    }
    else {
      result = this.value;
    }
    return result;
  }

  sort() {
    this.options = this.options.sort(
      (a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
          return -1;
        }
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        return 0;
      });
  }

  organizationFilterTaglimitText(count) {
    return `and ${count} other items`
  }

  asyncFindOrganizations(query, pageNumber = 1) {
    this.isLoading = true;

    this.searchText = query;
    this.pageNumber = pageNumber;

    this.fetchOrganizations().then(response => {
      this.pageNumber = response.pageNumber;
      this.pageSize = response.pageSize;

      if (this.pageNumber === 1) {
        this.options = response.data;
        this.sort();
      }
      else {
        this.options = this.options.concat(response.data);
        this.sort();
      }

      if (this.groupByOptionsByName) {
        const newOptionsWithNameOfAlreadySelectedOrganization =
          this.options.filter(x => this.value.some(y => x.name.toLowerCase() === y.name.toLowerCase()));     
      }

      this.noMoreOptions = this.options.length >= response.totalCount;
      this.isLoading = false;
    });
  }

  async fetchOrganizations(searchText = null, pageNumber = null, isSearchTextStrict = false): Promise<PageResult<Organization>> {
    let url = '/api/organization/getuserorganizationsV3';
    const params: { name: string, value: any }[] = [];

    pageNumber = pageNumber ?? this.pageNumber;
    if (pageNumber && pageNumber > 0) {
      params.push({ name: 'pageNumber', value: pageNumber });
    }

    if (this.pageSize && this.pageSize > 0) {
      params.push({ name: 'pageSize', value: this.pageSize });
    }

    searchText = searchText ?? this.searchText;
    if (searchText && searchText.trim() !== "") {
      params.push({ name: 'searchText', value: searchText.trim() });
    }

    if (this.parent?.id) {
      params.push({ name: 'parentId', value: this.parent.id });
    }

    if (this.isAdvertiserSearch) {
      params.push({ name: 'getAdvertisers', value: true });
    }

    if (!this.isAdvertiserSearch || this.isOrganizationSearch) {
      params.push({ name: 'getOrganisations', value: true });
    }

    params.push({ name: 'isSearchTextStrict', value: isSearchTextStrict });

    if (params.length > 0)
      url += '?' + params[0].name + '=' + params[0].value;
    for (let i = 1; i < params.length; i++) {
      url += '&' + params[i].name + '=' + params[i].value;
    }

    let result: PageResult<Organization> = null;
    try {
      const response = await this.authService.get(url);
      result = await response.data;
    }
    catch (error) {
      if (this.onError && typeof this.onError === 'function') {
        this.onError(error.response);
      }
    }

    return result;
  }

  async loadOrganizations(): Promise<void> {
    this.isLoading = true;
    await this.asyncFindOrganizations(this.searchText);

    this.sort();
    this.isLoading = false;
  }

  clearAllOrganizationFilterTags() {
    this.updateValue([]);
  }

  async asyncLoadMoreOrganizationFilterItems() {
    await this.asyncFindOrganizations(this.searchText, this.pageNumber + 1);
  }
}
